import { VehicleWithShortDriversOutput } from '@allocab/allocab-driver-shared-types';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { VehicleOptionsIndicator } from './VehicleOptionsIndicator';
import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { LicensePlate } from '../../components/LicensePlate';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { VehicleAssignButton } from '../../components/vehicle-assign-button/VehicleAssignButton';
import { useFethVehiclesPage } from '../../hooks/useFetchVehicles';
import { VEHICLES_LIMIT } from '../../hooks/useFetchVehiclesForDriver';
import { t } from '../../services/translation/translation';
import { formatDate, parseDate } from '../../utils/date';
import { getEngineTypeText } from '../../utils/vehicle';

const columns: TableColumn<VehicleWithShortDriversOutput>[] = [
  {
    name: t('common.licence-plate'),
    render: vehicle => (
      <Link to={`/vehicles/${vehicle.id}`}>
        <LicensePlate plateNumber={vehicle.licensePlate} />
      </Link>
    ),
    sortKey: 'licensePlate',
  },
  {
    name: t('common.status'),
    render: vehicle => (
      <div className="w-32">
        <BadgeIndicator variant={vehicle.validated ? 'green' : 'yellow'}>
          {vehicle.validated ? t('common.validated') : t('common.validating')}
        </BadgeIndicator>
      </div>
    ),
    sortKey: 'validated',
  },
  {
    name: t('common.range'),
    render: vehicle => (
      <span className="capitalize">{`${vehicle.brand?.toLowerCase() ?? ''} ${
        vehicle.range?.toLowerCase() ?? ''
      }`}</span>
    ),
    sortKey: 'brand',
  },
  {
    name: t('common.color'),
    render: vehicle => <span className="capitalize">{vehicle.color?.toLowerCase() ?? '--'}</span>,
    sortKey: 'color',
  },
  {
    name: t('common.panel'),
    render: vehicle => vehicle.panelKeys?.map(p => getPanelNameLabelByKey(p)).join(',') ?? '--',
  },
  {
    name: t('common.first-date'),
    render: vehicle =>
      vehicle.firstDate ? formatDate(parseDate(vehicle.firstDate, 'DD-MM-YYYY'), t('date.date-short')) : '--',
    sortKey: 'firstDate',
  },
  {
    name: t('common.engine-type'),
    render: vehicle => (vehicle.engineType ? getEngineTypeText(vehicle.engineType) : '--'),
    sortKey: 'engineType',
  },
  {
    name: t('vehicle-route.driver-count'),
    render: vehicle => vehicle.drivers.length,
  },
  {
    name: t('common.options'),
    render: vehicle => <VehicleOptionsIndicator vehicle={vehicle} />,
  },
  {
    name: t('common.assign'),
    render: vehicle =>
      vehicle.validated ? <VehicleAssignButton drivers={vehicle.drivers} vehicleId={vehicle.id} /> : null,
  },
];

export const VehiclesTable = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? undefined;
  const direction = (searchParams.get('direction') ?? undefined) as 'desc' | 'asc' | undefined;
  const { data, isLoading, isFetching, isPreviousData } = useFethVehiclesPage({ direction, orderBy }, currentPage);
  const nbPages = Math.ceil((data?.totalItems ?? 0) / VEHICLES_LIMIT);

  return (
    <div>
      <Table
        identifier="id"
        columns={columns}
        data={data?.items}
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
        title={t('vehicles-route.table-title')}
        caption={t('vehicles-route.caption')}
        action={<PrimaryButton text={t('common.add')} onClick={() => navigate('add')} />}
      />
    </div>
  );
};
